/**
 * Resolves the current user's role within the labeling factory UI.
 *
 * Four roles:
 * - "admin": platform admins (isAdmin flag) — full factory management.
 * - "manager": scoped users with the manage_labeling capability — factory
 *   management only for projects granted by the backend.
 * - "internal": a2z staff (by email domain) or backend-flagged internal
 *   user types (admin/client) who aren't flagged isAdmin but should still
 *   see internal-only views (e.g. impersonation, non-blinded stats).
 * - "annotator": everyone else — external contractors/radiologists.
 */

import { useAuth, useIsAdmin, useToken, useUserId } from "../context/AuthContext";
import { isInternalRole } from "../services/roles";
import { decodeToken } from "../utils/tokenUtils";

export type LabelingRole = "admin" | "manager" | "internal" | "annotator";

const INTERNAL_EMAIL_DOMAIN = "@a2zradiology.ai";

export interface ResolveLabelingRoleInput {
  isAdmin: boolean;
  userId: string | null;
  userType: string | null | undefined;
  capabilities?: readonly string[];
}

/** Pure resolver — no React/hook dependencies, easy to unit test. */
export function resolveLabelingRole({
  isAdmin,
  userId,
  userType,
  capabilities = [],
}: ResolveLabelingRoleInput): LabelingRole {
  if (isAdmin) return "admin";
  if (capabilities.includes("manage_labeling")) return "manager";

  const isInternalUserType = isInternalRole(userType);
  if (isInternalUserType) return "internal";

  // An explicit scoped role wins over the legacy directory-domain fallback.
  // This mirrors the backend and prevents a contractor/reviewer profile from
  // silently rendering staff-wide labeling views.
  if (userType) return "annotator";

  const isInternalDomain = userId?.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN) ?? false;
  if (isInternalDomain) return "internal";

  return "annotator";
}

/** Hook form — reads the pieces from AuthContext and resolves the role. */
export function useLabelingRole(): LabelingRole {
  const isAdmin = useIsAdmin();
  const userId = useUserId();
  const token = useToken();
  const { userType } = useAuth();
  const capabilities = token ? decodeToken(token)?.capabilities : undefined;

  return resolveLabelingRole({ isAdmin, userId, userType, capabilities });
}
